'use client';

import Image from '@/components/Image';
import Wish from '@/components/Product/Wish';
import type { ListProduct, WishProduct } from '@/api/types/public';
import { useAppNavigation } from '@/hooks/useAppNavigation';
import { useTranslations } from '@/i18n/use-translations';
import { useStore } from '@/providers/app-store-provider';
import { formatPriceNumberWithSymbol, priceInfo as getPriceInfo } from '@/utils/price';
import { KeyboardEvent, RefObject, memo, useEffect, useMemo, useState } from 'react';
import { useValidExposure } from '@/hooks/useValidExposure';
import { getStyleHotProductColorStateKey } from '../hooks/styleHotProductViewState';
import type { StyleHotProductCategoryProduct } from '../types';
import { getPcStyleProductCardImages } from './styleProductCardImages';

const FALLBACK_CURRENCY = {
  name: 'USD',
  full_symbol: '$',
  symbol: '$',
  exchange_rate: '1.000000',
  symbol_prefix: '$',
  symbol_suffix: '',
} as const;

interface StyleProductListProps {
  products?: StyleHotProductCategoryProduct[];
  tabName?: string;
  firstRowRef?: RefObject<HTMLDivElement | null>;
  isLoading?: boolean;
  selectedColorByProductKey?: Record<string, number>;
  onProductClick?: (item: StyleHotProductCategoryProduct, index: number) => void | Promise<void>;
  onProductColorChange?: (item: StyleHotProductCategoryProduct, colorIndex: number) => void;
  onProductExposure?: (item: StyleHotProductCategoryProduct, index: number) => void;
}

interface StyleProductCardProps {
  product: StyleHotProductCategoryProduct;
  variant: 'large' | 'small';
  index: number;
  initialColorIndex?: number;
  onProductClick?: (item: StyleHotProductCategoryProduct, index: number) => void | Promise<void>;
  onProductColorChange?: (item: StyleHotProductCategoryProduct, colorIndex: number) => void;
}

type WishProductData = (ListProduct | WishProduct) & {
  business_sku?: string;
  sku?: string;
};

type StyleProductColorItem = NonNullable<StyleHotProductCategoryProduct['correlation']>[number];

function mergeCorrelationProduct(
  product: StyleHotProductCategoryProduct,
  correlationItem?: StyleProductColorItem,
): StyleHotProductCategoryProduct {
  if (!correlationItem) {
    return product;
  }

  return {
    ...product,
    ...correlationItem,
    pc_image_type: product.pc_image_type,
    wap_image_type: product.wap_image_type,
    correlation: product.correlation,
    fine_ranking_type: product.fine_ranking_type,
    recall_group_type: product.recall_group_type,
  } as unknown as StyleHotProductCategoryProduct;
}

function getProductName(product: StyleHotProductCategoryProduct): string {
  return product.short_name || product.name || product.shop_sku || '';
}

function getStyleProductRowStartIndex(productIndex: number): number {
  return productIndex < 3 ? 0 : 3 + Math.floor((productIndex - 3) / 4) * 4;
}

function isSoldOutProduct(product: StyleHotProductCategoryProduct): boolean {
  const stock = Number(product.stock);

  return !Number.isNaN(stock) && stock <= 0;
}

function isOffShelfProduct(product: StyleHotProductCategoryProduct): boolean {
  const status = Number(product.status);

  return !Number.isNaN(status) && status !== 1;
}

function normalizeProductList(
  products?: StyleHotProductCategoryProduct[],
): StyleHotProductCategoryProduct[] {
  return (Array.isArray(products) ? products : [])
    .filter((product) => product?.id && !isOffShelfProduct(product))
    .map((product, index) => ({ product, index }))
    .sort((a, b) => {
      const soldOutDiff = Number(isSoldOutProduct(a.product)) - Number(isSoldOutProduct(b.product));

      return soldOutDiff || a.index - b.index;
    })
    .map(({ product }) => product);
}

function StyleProductColorDots({
  product,
  selectedIndex,
  onColorClick,
  onColorMouseEnter,
  onColorMouseLeave,
}: {
  product: StyleHotProductCategoryProduct;
  selectedIndex: number;
  onColorClick: (index: number) => void;
  onColorMouseEnter: (item: StyleProductColorItem) => void;
  onColorMouseLeave: () => void;
}) {
  const correlations = Array.isArray(product.correlation) ? product.correlation : [];
  const visibleColors = correlations.slice(0, 4);
  const displayColorNum = Number(
    (product as Record<string, unknown>).display_color_num || correlations.length || 0,
  );
  const restCount = Math.max(displayColorNum - visibleColors.length, 0);

  if (visibleColors.length === 0 && restCount === 0) {
    return null;
  }

  return (
    <div className="mt-[16px] flex h-[20px] items-center gap-[8px]">
      {visibleColors.map((item, index) => {
        const colorImage = (item as Record<string, string | undefined>)
          .product_frame_color_main_url;
        const colorValue =
          (item as Record<string, string | undefined>).product_frame_color ||
          (item as Record<string, string | undefined>).color;
        const colorName =
          (item as Record<string, string | undefined>).product_frame_color_name || '';
        const isSelected = index === selectedIndex;

        return (
          <span
            className="flex h-[20px] w-[20px] items-center justify-center"
            key={item.id || `${colorImage || colorValue || index}-${index}`}
          >
            <span
              role="radio"
              aria-checked={isSelected}
              aria-label={colorName}
              tabIndex={0}
              className={`flex h-[20px] w-[20px] cursor-pointer items-center justify-center rounded-full border border-transparent ${
                isSelected
                  ? 'border-primary-purple-active'
                  : 'hover:border-auxiliary-light-gray-blue'
              }`}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                onColorClick(index);
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  event.stopPropagation();
                  onColorClick(index);
                }
              }}
              onMouseEnter={() => onColorMouseEnter(item)}
              onMouseLeave={onColorMouseLeave}
            >
              <span
                className="block h-[16px] w-[16px] rounded-full border border-white bg-[#d8d8d8] shadow-[0_0_0_1px_rgba(0,0,0,0.08)]"
                style={{
                  backgroundColor: colorImage ? undefined : colorValue || '#d8d8d8',
                  backgroundImage: colorImage ? `url(${colorImage})` : undefined,
                  backgroundPosition: 'center',
                  backgroundRepeat: 'no-repeat',
                  backgroundSize: 'cover',
                }}
              />
            </span>
          </span>
        );
      })}
      {restCount > 0 && (
        <span className="font-br text-[12px] leading-[14px] text-[#808080]">+{restCount}</span>
      )}
    </div>
  );
}

const ExposureDetector = memo(function ExposureDetector({
  item,
  index,
  onExposure,
}: {
  item: StyleHotProductCategoryProduct;
  index: number;
  onExposure?: (item: StyleHotProductCategoryProduct, index: number) => void;
}) {
  const { ref } = useValidExposure({
    callback: () => item.id && onExposure?.(item, index),
    ts: item.id,
  });

  return <div ref={ref} className="pointer-events-none absolute inset-0" />;
});

function StyleProductCard({
  product,
  variant,
  index,
  initialColorIndex = 0,
  onProductClick,
  onProductColorChange,
}: StyleProductCardProps) {
  const { navigate } = useAppNavigation();
  const t = useTranslations('product');
  const currency = useStore((state) => state.currency) || FALLBACK_CURRENCY;
  const userInfo = useStore((state) => state.userInfo);
  const correlations = useMemo(
    () => (Array.isArray(product.correlation) ? product.correlation : []),
    [product.correlation],
  );
  const [selectedColorIndex, setSelectedColorIndex] = useState(initialColorIndex);
  const [previewProduct, setPreviewProduct] = useState<StyleHotProductCategoryProduct | null>(null);
  const selectedProduct = useMemo(
    () => mergeCorrelationProduct(product, correlations[selectedColorIndex]),
    [correlations, product, selectedColorIndex],
  );
  const displayProduct = previewProduct || selectedProduct;
  const { productImage, hoverProductImage, shouldShowHoverProductImage } =
    getPcStyleProductCardImages(displayProduct);
  const productName = getProductName(displayProduct);
  const isSoldOut = isSoldOutProduct(displayProduct);
  const priceData = getPriceInfo(userInfo, displayProduct, currency as never) as {
    price?: string;
    sell_price?: string;
  };
  const sellPrice = priceData.sell_price || priceData.price || '';
  const originalPrice = priceData.price || '';
  const isLarge = variant === 'large';
  const imageShouldCover = productImage.kind === 'celebrity' || productImage.kind === 'material';
  const productImagePaddingClassName = isLarge ? 'px-[9.88%] py-[30%]' : 'px-[11.26%] py-[30.63%]';
  const containImageWidth = isLarge ? 479 : 344;
  const containImageHeight = isLarge ? 239 : 172;

  useEffect(() => {
    setSelectedColorIndex(initialColorIndex);
    setPreviewProduct(null);
  }, [initialColorIndex, product.id]);

  const handleCardClick = async () => {
    await onProductClick?.(displayProduct, index);

    if (displayProduct.shop_sku) {
      navigate(`/goods-detail/${displayProduct.shop_sku}`);
    }
  };

  const handleCardKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      void handleCardClick();
    }
  };

  return (
    <article className="relative w-full overflow-hidden rounded-[12px] bg-[#eeeeee]">
      <div
        role="link"
        tabIndex={0}
        className="block h-full w-full cursor-pointer"
        aria-label={productName}
        onClick={handleCardClick}
        onKeyDown={handleCardKeyDown}
      >
        <div
          className={`relative aspect-square w-full bg-[#eeeeee] ${
            shouldShowHoverProductImage ? 'group' : ''
          } ${
            imageShouldCover
              ? ''
              : `flex items-center justify-center ${productImagePaddingClassName}`
          }`}
        >
          {productImage.src && imageShouldCover && (
            <Image
              src={productImage.src}
              alt={productName}
              fill
              sizes={isLarge ? '33vw' : '25vw'}
              className={`h-full w-full object-cover ${isSoldOut ? 'opacity-50' : ''}`}
            />
          )}
          {productImage.src && !imageShouldCover && (
            <Image
              src={productImage.src}
              alt={productName}
              width={containImageWidth}
              height={containImageHeight}
              sizes={isLarge ? '479px' : '344px'}
              className={`h-auto max-h-full w-full object-contain ${
                productImage.kind === 'product' ? 'mix-blend-multiply' : ''
              } ${isSoldOut ? 'opacity-50' : ''}`}
            />
          )}
          {shouldShowHoverProductImage && (
            <div
              className={`absolute inset-0 flex items-center justify-center bg-[#eeeeee] opacity-0 transition-opacity duration-300 group-hover:opacity-100 ${productImagePaddingClassName}`}
            >
              <Image
                src={hoverProductImage.src}
                alt={`${productName} product`}
                width={containImageWidth}
                height={containImageHeight}
                sizes={isLarge ? '479px' : '344px'}
                className={`h-auto max-h-full w-full object-contain ${
                  hoverProductImage.kind === 'product' ? 'mix-blend-multiply' : ''
                } ${isSoldOut ? 'opacity-50' : ''}`}
              />
            </div>
          )}
          {isSoldOut && (
            <span className="pointer-events-none absolute left-1/2 top-1/2 z-[2] flex h-[110px] w-[110px] -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-black/60 text-center">
              <span
                className="font-bs text-[18px] leading-[22px] text-white"
                dangerouslySetInnerHTML={{ __html: t('soldOut.soldOut') }}
              />
            </span>
          )}
        </div>

        <div className={`bg-[#eeeeee] px-[24px] py-[16px] ${isLarge ? 'h-[122px]' : 'h-[118px]'}`}>
          <p
            className={`line-clamp-1 font-br text-[#282828] ${
              isLarge ? 'text-[18px] leading-[22px]' : 'text-[16px] leading-[18px]'
            }`}
          >
            {productName}
          </p>
          <div
            className={`mt-[8px] flex h-[24px] items-center ${isLarge ? 'gap-[6px]' : 'gap-[4.5px]'}`}
          >
            {sellPrice && (
              <span className="notranslate font-bb text-[20px] leading-[24px] text-[#282828]">
                {formatPriceNumberWithSymbol(sellPrice, currency as never)}
              </span>
            )}
            {originalPrice && originalPrice !== sellPrice && (
              <span className="notranslate font-br text-[20px] leading-[24px] text-[#808080] line-through">
                {formatPriceNumberWithSymbol(originalPrice, currency as never)}
              </span>
            )}
          </div>
          <StyleProductColorDots
            product={product}
            selectedIndex={selectedColorIndex}
            onColorClick={(index) => {
              setSelectedColorIndex(index);
              setPreviewProduct(null);
              onProductColorChange?.(product, index);
            }}
            onColorMouseEnter={(item) => setPreviewProduct(mergeCorrelationProduct(product, item))}
            onColorMouseLeave={() => setPreviewProduct(null)}
          />
        </div>
      </div>

      <Wish
        data={displayProduct as unknown as WishProductData}
        className="absolute right-[20px] top-[20px] z-[3] leading-[24px] text-auxiliary-dark-gray"
        iconClassName="text-[22px]"
        iconSize="22px"
        showWishNum={false}
        loginSituation="周年庆风格爆品收藏"
        collectScene="周年庆风格爆品"
        price={sellPrice}
      />
    </article>
  );
}

function StyleProductSkeletonList({ firstRowRef }: Pick<StyleProductListProps, 'firstRowRef'>) {
  return (
    <div className="flex w-full flex-col gap-[16px]" aria-busy="true">
      <div ref={firstRowRef} className="grid w-full grid-cols-3 gap-[16.5px]">
        {Array.from({ length: 3 }).map((_, index) => (
          <div
            className="w-full overflow-hidden rounded-[12px] bg-[#eeeeee]/70"
            key={`style-product-skeleton-large-${index}`}
          >
            <div className="aspect-square w-full animate-pulse bg-[#eeeeee]/70" />
            <div className="h-[122px] w-full animate-pulse bg-[#eeeeee]/70" />
          </div>
        ))}
      </div>
      <div className="grid w-full grid-cols-4 gap-[16px]">
        {Array.from({ length: 4 }).map((_, index) => (
          <div
            className="w-full overflow-hidden rounded-[12px] bg-[#eeeeee]/60"
            key={`style-product-skeleton-small-${index}`}
          >
            <div className="aspect-square w-full animate-pulse bg-[#eeeeee]/60" />
            <div className="h-[118px] w-full animate-pulse bg-[#eeeeee]/60" />
          </div>
        ))}
      </div>
    </div>
  );
}

export default function StyleProductList({
  products,
  tabName,
  firstRowRef,
  isLoading = false,
  selectedColorByProductKey = {},
  onProductClick,
  onProductColorChange,
  onProductExposure,
}: StyleProductListProps) {
  const productList = useMemo(() => normalizeProductList(products), [products]);
  const largeProducts = productList.slice(0, 3);
  const restProducts = productList.slice(3);

  if (isLoading) {
    return <StyleProductSkeletonList firstRowRef={firstRowRef} />;
  }

  if (productList.length === 0) {
    return null;
  }

  return (
    <div className="flex w-full flex-col gap-[16px]">
      {largeProducts.length > 0 && (
        <div
          ref={firstRowRef}
          className="grid w-full grid-cols-3 gap-[16.5px]"
          data-style-product-row-start={0}
        >
          {largeProducts.map((product, index) => (
            <div key={`${tabName}-${product.id || product.shop_sku}-${index}`} className="relative">
              <StyleProductCard
                product={product}
                variant="large"
                index={index}
                initialColorIndex={
                  selectedColorByProductKey[getStyleHotProductColorStateKey(product)] || 0
                }
                onProductClick={onProductClick}
                onProductColorChange={onProductColorChange}
              />
              <ExposureDetector item={product} index={index} onExposure={onProductExposure} />
            </div>
          ))}
        </div>
      )}

      {restProducts.length > 0 && (
        <>
          {Array.from({ length: Math.ceil(restProducts.length / 4) }, (_, rowIndex) => {
            const rowProducts = restProducts.slice(rowIndex * 4, rowIndex * 4 + 4);
            const rowStartIndex = getStyleProductRowStartIndex(rowIndex * 4 + 3);

            return (
              <div
                className="grid w-full grid-cols-4 gap-[16px]"
                data-style-product-row-start={rowStartIndex}
                key={`${tabName}-row-${rowStartIndex}`}
              >
                {rowProducts.map((product, index) => {
                  const productIndex = rowStartIndex + index;

                  return (
                    <div
                      key={`${tabName}-${product.id || product.shop_sku}-${productIndex}`}
                      className="relative"
                    >
                      <StyleProductCard
                        product={product}
                        variant="small"
                        index={productIndex}
                        initialColorIndex={
                          selectedColorByProductKey[getStyleHotProductColorStateKey(product)] || 0
                        }
                        onProductClick={onProductClick}
                        onProductColorChange={onProductColorChange}
                      />
                      <ExposureDetector
                        item={product}
                        index={productIndex}
                        onExposure={onProductExposure}
                      />
                    </div>
                  );
                })}
              </div>
            );
          })}
        </>
      )}
    </div>
  );
}
